import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';
import { useProducts } from '@/hooks/useProducts';
import type { Product } from '@/types/product';

type SortOption = 'featured' | 'low-high' | 'high-low' | 'newest';
type ViewMode = 'grid' | 'list';

const ITEMS_PER_PAGE = 8;

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1200]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [selectedBadge, setSelectedBadge] = useState('all');
  const [sortBy, setSortBy] = useState<SortOption>('featured');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const { products: allProducts, loading } = useProducts();

  useEffect(() => {
    document.title = 'Shop All Products - Beauty, Kitchen & Electronics | AiShopio Dubai';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Browse all products at AiShopio Dubai. Shop beauty essentials, kitchen gadgets, health products & electronics. Filter by category, price & rating. Free UAE delivery over AED 200.');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Shop All Products - AiShopio Dubai',
      description: 'Browse all premium products at AiShopio Dubai including beauty, kitchen, health and electronics.',
      url: `${siteUrl}/shop`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Shop', item: `${siteUrl}/shop` },
        ],
      },
    };

    const existing = document.querySelector('script[data-schema="shop"]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'shop');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { document.querySelector('script[data-schema="shop"]')?.remove(); };
  }, []);

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setSelectedCategory(cat);
  }, [searchParams]);

  const filtered = useMemo(() => {
    let result = [...allProducts];
    if (searchQuery) result = result.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.description || '').toLowerCase().includes(searchQuery.toLowerCase()));
    if (selectedCategory !== 'all') result = result.filter(p => p.category === selectedCategory);
    result = result.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    if (selectedRating > 0) result = result.filter(p => p.rating >= selectedRating);
    if (selectedBadge !== 'all') result = result.filter(p => p.badge === selectedBadge);
    switch (sortBy) {
      case 'low-high': result.sort((a, b) => a.price - b.price); break;
      case 'high-low': result.sort((a, b) => b.price - a.price); break;
      case 'newest': result.sort((a, b) => b.id - a.id); break;
    }
    return result;
  }, [allProducts, searchQuery, selectedCategory, priceRange, selectedRating, selectedBadge, sortBy]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    if (cat !== 'all') setSearchParams({ category: cat });
    else setSearchParams({});
  };

  const categories = [
    { key: 'all', label: 'All Products', icon: 'ri-apps-line' },
    { key: 'beauty', label: 'Beauty', icon: 'ri-magic-line' },
    { key: 'health', label: 'Health', icon: 'ri-heart-pulse-line' },
    { key: 'kitchen', label: 'Kitchen', icon: 'ri-restaurant-line' },
    { key: 'electronics', label: 'Electronics', icon: 'ri-smartphone-line' },
  ];

  const FilterPanel = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-filter-3-line text-orange-500"></i> Category
        </h3>
        <div className="space-y-2">
          {categories.map(cat => (
            <button
              key={cat.key}
              onClick={() => handleCategoryChange(cat.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer ${
                selectedCategory === cat.key
                  ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                  : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.label}
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${selectedCategory === cat.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                {cat.key === 'all' ? allProducts.length : allProducts.filter(p => p.category === cat.key).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-price-tag-3-line text-orange-500"></i> Price (AED)
        </h3>
        <div className="space-y-3">
          <input
            type="range"
            min={0}
            max={1200}
            value={priceRange[1]}
            onChange={(e) => { setPriceRange([priceRange[0], Number(e.target.value)]); setCurrentPage(1); }}
            className="w-full accent-orange-500"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>AED {priceRange[0]}</span>
            <span>AED {priceRange[1]}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[[0, 200], [200, 500], [500, 800], [800, 1200]].map(([min, max]) => (
              <button
                key={`${min}-${max}`}
                onClick={() => { setPriceRange([min, max]); setCurrentPage(1); }}
                className={`text-xs px-2 py-1.5 rounded-lg border transition-all duration-200 cursor-pointer ${
                  priceRange[0] === min && priceRange[1] === max
                    ? 'border-orange-500 bg-orange-50 text-orange-600 font-medium'
                    : 'border-gray-200 text-gray-600 hover:border-orange-300'
                }`}
              >
                {min}–{max} AED
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-star-line text-orange-500"></i> Min Rating
        </h3>
        <div className="space-y-2">
          {[0, 4, 4.5, 4.7].map(r => (
            <button
              key={r}
              onClick={() => { setSelectedRating(r); setCurrentPage(1); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all duration-200 cursor-pointer ${
                selectedRating === r ? 'bg-orange-50 text-orange-600 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {r === 0 ? (
                <span>All Ratings</span>
              ) : (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`ri-star-${i < Math.floor(r) ? 'fill' : 'line'} text-amber-400 text-xs`}></i>
                  ))}
                  <span>{r}+</span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-award-line text-orange-500"></i> Badge
        </h3>
        <div className="flex flex-wrap gap-2">
          {['all', 'HOT', 'SALE', 'NEW'].map(b => (
            <button
              key={b}
              onClick={() => { setSelectedBadge(b); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                selectedBadge === b
                  ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-600'
              }`}
            >
              {b === 'all' ? 'All' : b}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={() => { setSelectedCategory('all'); setPriceRange([0, 1200]); setSelectedRating(0); setSelectedBadge('all'); setSortBy('featured'); setCurrentPage(1); setSearchParams({}); }}
        className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:border-orange-400 hover:text-orange-500 transition-all duration-200 cursor-pointer"
      >
        <i className="ri-refresh-line mr-2"></i>Clear Filters
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />

      <div className="pt-24 pb-8 px-4 sm:px-6" style={{ background: 'linear-gradient(135deg, #fff7ed, #fdf2f8)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
                Shop All Products
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line mx-1"></i>
                <span className="text-gray-800">Shop</span>
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="Search products..."
                className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-200 bg-white focus:outline-none focus:border-orange-400 text-sm text-gray-800"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 sticky top-24">
              <FilterPanel />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 text-sm font-medium text-gray-700 hover:border-orange-400 hover:text-orange-500 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line"></i> Filters
                </button>
                <span className="text-sm text-gray-500">
                  {loading ? (
                    <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin text-orange-500"></i> Loading...</span>
                  ) : (
                    <><span className="font-semibold text-gray-800">{filtered.length}</span> products found</>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value as SortOption); setCurrentPage(1); }}
                  className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-orange-400 bg-white cursor-pointer"
                >
                  <option value="featured">Featured</option>
                  <option value="low-high">Price: Low to High</option>
                  <option value="high-low">Price: High to Low</option>
                  <option value="newest">Newest First</option>
                </select>
                <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`w-8 h-8 flex items-center justify-center rounded-full transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white text-orange-500' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <i className="ri-grid-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`w-8 h-8 flex items-center justify-center rounded-full transition-all cursor-pointer ${viewMode === 'list' ? 'bg-white text-orange-500' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <i className="ri-list-check text-sm"></i>
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white/70 rounded-3xl border border-gray-100/50 overflow-hidden animate-pulse">
                    <div className="h-52 bg-gray-200"></div>
                    <div className="p-4 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-5 bg-gray-200 rounded w-1/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : paginated.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-orange-50">
                  <i className="ri-search-line text-4xl text-orange-400"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No products found</h3>
                <p className="text-gray-500 mb-6">Try adjusting your filters or search query</p>
                <button
                  onClick={() => { setSelectedCategory('all'); setPriceRange([0, 1200]); setSelectedRating(0); setSelectedBadge('all'); setSearchQuery(''); }}
                  className="px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold cursor-pointer whitespace-nowrap"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5'
                : 'flex flex-col gap-4'
              }>
                {paginated.map((product, idx) => (
                  <ProductCard key={product.id} product={product} viewMode={viewMode} idx={idx} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 sm:gap-2 mt-10 flex-wrap">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                      currentPage === i + 1
                        ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white'
                        : 'border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {filterDrawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFilterDrawerOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Filters</h2>
              <button onClick={() => setFilterDrawerOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>
            <FilterPanel />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function ProductCard({ product, viewMode, idx }: { product: Product; viewMode: ViewMode; idx: number }) {
  const discount = Math.round(((product.original_price - product.price) / product.original_price) * 100);
  const badgeColors: Record<string, string> = {
    HOT: 'from-red-500 to-orange-500',
    SALE: 'from-orange-500 to-pink-600',
    NEW: 'from-green-500 to-teal-500',
  };

  if (viewMode === 'list') {
    return (
      <div
        className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-4 flex gap-4 hover:border-orange-300 transition-all duration-300 group"
        style={{ animationDelay: `${idx * 50}ms` }}
      >
        <div className="relative w-28 h-28 flex-shrink-0 rounded-2xl overflow-hidden">
          <img src={product.image || ''} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
          <span className={`absolute top-2 left-2 text-white text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${badgeColors[product.badge]}`}>
            {product.badge}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-800 text-sm mb-1 truncate">{product.name}</h3>
          <p className="text-gray-500 text-xs mb-2 line-clamp-2">{product.description}</p>
          <div className="flex items-center gap-1 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-amber-400 text-xs`}></i>
            ))}
            <span className="text-xs text-gray-500 ml-1">({product.reviews})</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-black text-gray-800">AED {product.price}</span>
              <span className="text-xs text-gray-400 line-through ml-2">AED {product.original_price}</span>
              <span className="text-xs text-green-600 font-semibold ml-2">-{discount}%</span>
            </div>
            <Link
              to={`/product/${product.id}`}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white text-xs font-semibold hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/product/${product.id}`}
      className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 overflow-hidden hover:border-orange-300 hover:scale-[1.02] transition-all duration-300 group block"
      style={{ animationDelay: `${idx * 50}ms` }}
    >
      <div className="relative h-52 overflow-hidden">
        <img src={product.image || ''} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
        <span className={`absolute top-3 left-3 text-white text-xs font-bold px-3 py-1 rounded-full bg-gradient-to-r ${badgeColors[product.badge]}`}>
          {product.badge}
        </span>
        <span className="absolute top-3 right-3 bg-white/90 text-green-600 text-xs font-bold px-2 py-1 rounded-full">
          -{discount}%
        </span>
      </div>
      <div className="p-4">
        <p className="text-xs text-orange-500 font-medium capitalize mb-1">{product.category}</p>
        <h3 className="font-bold text-gray-800 text-sm mb-2 line-clamp-2">{product.name}</h3>
        <div className="flex items-center gap-1 mb-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-amber-400 text-xs`}></i>
          ))}
          <span className="text-xs text-gray-500 ml-1">({product.reviews})</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-lg font-black text-gray-800">AED {product.price}</span>
            <span className="text-xs text-gray-400 line-through ml-1">AED {product.original_price}</span>
          </div>
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white group-hover:scale-110 transition-transform">
            <i className="ri-arrow-right-line text-sm"></i>
          </div>
        </div>
      </div>
    </Link>
  );
}
